import { useLocation, useNavigate } from 'react-router-dom';
import Chip from '../../../components/Chip';
import { useGetSupportTicketsById, useMarkTicketAsResolved } from '../../../queries/partner';
import { ActionButton } from '../../../components/Forms/ActionButton';
import { renderPriorityColor } from '../../../utils/chipColors/priorityColor';
import { renderActiveResolvedColor } from '../../../utils/chipColors/activeResolved';
import Modal from '../../Submissions/components/Modal';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ViewSupportTicket = () => {

    const { state } = useLocation();
    const navigate = useNavigate();
    const ticketId = state?.requestId;

    const [resolveModalOpen, setResolveModalOpen] = useState(false);

    const { data: ticketData, isPending } = useGetSupportTicketsById(ticketId);
    const { mutateAsync: markResolved, isPending: isMarkingResolved } = useMarkTicketAsResolved();

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm({
        defaultValues: {
            message: '',
        }
    });
    
    const onSubmit = async (data) => {
        console.log({
            ticketId: ticketId,
            ...data,
        });
        await markResolved({
            ticketId: ticketId,
            ...data,
        }, {
            onSuccess: (res) => {
                if(res.error){
                    toast.error('Something went wrong!');
                    return;
                }
                setResolveModalOpen(false);
                toast.success('Support ticket resolved!');
                setTimeout(() => {
                    navigate('/requests/support-tickets');
                }, 2000);
            }
        });
    }
    
    if (isPending) {
        return (
            <div className="flex items-start justify-center py-5 bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)]">
                <p className="text-center">Loading...</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen w-full font-semibold text-base overflow-visible">
            <section className="flex flex-col justify-center px-5 pt-4 py-9 gap-3 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] overflow-visible">
                <h1 className="text-xl text-primary font-bold">Support Ticket Details</h1>
                <div className="flex flex-col gap-4">
                    <div className="flex gap-1">
                        <label className="font-semibold text-primary">Campaign:</label>
                        <p>{ticketData?.campaign?.title ?? '-'}</p>
                    </div>
                    <div className="flex gap-1">
                        <label className="font-semibold text-primary">Issue Type:</label>
                        <p>{ticketData.issueType || '-'}</p>
                    </div>
                    <div className='flex items-center justify-between w-96'>
                        <div className="flex gap-2">
                            <label className="font-semibold text-primary">Status:</label>
                            <Chip value={ticketData?.active ? 'Active' : 'Resolved'} color={renderActiveResolvedColor(ticketData?.active)} />
                        </div>
                        <div className="flex gap-2">
                            <label className="font-semibold text-primary">Priority:</label>
                            <Chip value={ticketData?.priority || '-'} color={renderPriorityColor(ticketData?.priority)} />
                        </div>
                    </div>
                    <div className="flex gap-1">
                        <label className="font-semibold text-primary">Description:</label>
                        {/* <p dangerouslySetInnerHTML={{ __html: ticketData.description }}></p> */}
                        <p>{ticketData.description || '-'}</p>
                    </div>
                    <div className="flex gap-1">
                        <label className="font-semibold text-primary">Ticket Created Date:</label>
                        <p>{new Date(ticketData.createdAt).toLocaleDateString()}</p>
                    </div>
                    {
                        ticketData?.resolvedAt && (
                            <div className="flex gap-1">
                                <label className="font-semibold text-primary">Ticket Resolved Date:</label>
                                <p>{new Date(ticketData.resolvedAt).toLocaleDateString()}</p>
                            </div>
                        )
                    }
                    {ticketData.NGOId && (
                        <div className="flex gap-1">
                            <label className="font-semibold text-primary">NGO Name:</label>
                            <p>{ticketData.NGOId.ngoName}</p>
                        </div>
                    )}
                    {ticketData.companyId && (
                        <div className="flex gap-1">
                            <label className="font-semibold text-primary">Company Name:</label>
                            <p>{ticketData.companyId.companyName}</p>
                        </div>
                    )}
                    <div className='flex flex-col gap-1'>
                        <label className="font-semibold text-primary">Media:</label>
                        {ticketData.image ? (
                            <img
                                src={ticketData.image}
                                alt="Campaign Media"
                                className="w-48 h-48 object-cover rounded cursor-pointer"
                                onClick={() => window.open(ticketData.image, '_blank')}
                            />
                        ) : (
                            <p className="text-sm">No media uploaded</p>
                        )}
                    </div>
                    {!ticketData.active && ticketData.resolveComment && (
                        <div className="flex gap-1">
                            <label className="font-semibold text-primary text-nowrap">Resolution Comment:</label>
                            <p>{ticketData.resolveComment}</p>
                        </div>
                    )}
                </div>
                <Modal isOpen={resolveModalOpen} onClose={() => setResolveModalOpen(false)}>
                    <h1>Do you really want to mark as resolved?</h1>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="flex flex-col items-start justify-end gap-1 mt-5">
                            <p>Message</p>
                            <textarea
                                type="number"
                                className="border border-border rounded-md p-2 w-full text-base outline-none focus:outline-none resize-none"
                                rows={7}
                                {...register('message', {
                                    required: 'Message is required.'
                                })}
                            />
                        </div>
                        <div className='flex items-center justify-end mt-5'>
                            <ActionButton
                                text="Mark as Resolved"
                                variant={'submit'}
                                disabled={isMarkingResolved || ticketData?.active === false}
                            />
                        </div>
                    </form>
                </Modal>
                {
                    ticketData?.active && (
                        <div className='flex items-center justify-end mt-2'>
                            <ActionButton
                                text="Mark as Resolved"
                                variant={'submit'}
                                disabled={isMarkingResolved || ticketData?.active === false}
                                handleSubmit={() => setResolveModalOpen(true)}
                            />
                        </div>
                    )
                }
            </section>
            <ToastContainer
                position="top-right"
            />
        </div>
    );
}

export default ViewSupportTicket;